<template>
  <div class="image-gen-ai">
    <div class="image-gen-ai__header">
      <h2>AI Image Generator</h2>
      <p>Enter a prompt to generate an image using AI</p>
    </div>

    <div class="image-gen-ai__form">
      <cvt-input
        placeholder="Describe the image you want to generate..."
        type="textarea"
        label="Image Prompt"
        rows="4"
        class="image-gen-ai__textarea"
        :disabled="isGenerating"
        @input="onPromptChange"
      />

      <cvt-button
        :disabled="!canGenerate"
        :loading="isGenerating"
        class="image-gen-ai__generate-btn"
        color="primary"
        :text="isGenerating ? 'Please wait...' : 'Generate'"
        @click="generateImage"
      />
    </div>

    <div v-if="isGenerating || generatedImage" class="image-gen-ai__preview">
      <h3 v-if="generatedImage && generatedImage.url && !isGenerating">{{ isGenerating ? 'Generating Image' : 'Generated Image' }}</h3>

      <!-- Loading State -->
      <div v-if="isGenerating" class="image-gen-ai__loading">
        <cvt-loading-message message="Generating your image" :display="true" />
        <div class="image-gen-ai__loading-placeholder">
          <div class="image-gen-ai__spinner"></div>
        </div>
      </div>

      <!-- Generated Image -->
      <div
        v-else-if="generatedImage"
        class="image-gen-ai__image-container"
        @mouseenter="showUseButton = true"
        @mouseleave="showUseButton = false"
      >
        <img
          :src="generatedImage && generatedImage.url"
          :alt="generatedImage && generatedImage.prompt"
          class="image-gen-ai__image"
        />
        <div v-if="showUseButton" class="image-gen-ai__overlay">
          <cvt-button
            class="image-gen-ai__use-btn"
            color="success"
            text="Use Image"
            @click="useImage"
          >
          </cvt-button>
        </div>
      </div>
    </div>

    <div v-if="error" class="image-gen-ai__error">
      <p>{{ error }}</p>
    </div>
  </div>
</template>

<script>
import { V1ImageGenAI } from '../../store/services/api'

const imageGenAPI = new V1ImageGenAI()

export default {
  name: 'ImageGenAI',
  props: {
    privateUser: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      prompt: '',
      isGenerating: false,
      generatedImage: null,
      showUseButton: false,
      error: null,
    }
  },
  computed: {
    canGenerate() {
      return this.prompt && this.prompt.trim().length > 0 && !this.isGenerating
    },
  },
  methods: {
    onPromptChange({ target }) {
      this.prompt = target.value
    },
    async generateImage() {
      if (!this.prompt.trim()) return

      this.isGenerating = true
      this.error = null

      try {
        const response = await imageGenAPI.generateImage(
          this.prompt,
          this.privateUser,
        )
        this.generatedImage = response.data
      } catch (error) {
        console.error('Failed to generate image:', error)
        this.error = 'Failed to generate image. Please try again.'
      } finally {
        this.isGenerating = false
      }
    },

    useImage() {
      if (this.generatedImage) {
        this.$emit('use-image',{...this.generatedImage})
      }
      this.generatedImage = null
    },
  },
}
</script>

<style scoped lang="scss">
.image-gen-ai {
  padding: 20px;
  max-width: 600px;
  margin: 0 auto;

  &__header {
    text-align: center;
    margin-bottom: 30px;

    h2 {
      margin: 0 0 10px 0;
      color: #333;
    }

    p {
      margin: 0;
      color: #666;
      font-size: 14px;
    }
  }

  &__form {
    margin-bottom: 30px;
  }

  &__textarea {
    margin-bottom: 20px;
  }

  &__generate-btn {
    width: 50%;
    margin-left: auto;
  }

  &__preview {
    text-align: center;

    h3 {
      margin: 0 0 20px 0;
      color: #333;
    }
  }

  &__loading {
    margin: 20px 0;
  }

  &__loading-placeholder {
    width: 300px;
    height: 300px;
    margin: 20px auto;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #007bff;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  &__image-container {
    position: relative;
    display: inline-block;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    margin-bottom: 15px;
  }

  &__image {
    display: block;
    max-width: 100%;
    height: auto;
    border-radius: 8px;
  }

  &__overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
  }

  &__use-btn {
    padding: 12px 24px;
  }

  &__prompt-text {
    margin: 0;
    color: #666;
    font-size: 14px;
    font-style: italic;
  }

  &__error {
    background: #fee;
    border: 1px solid #fcc;
    border-radius: 4px;
    padding: 15px;
    margin-top: 20px;
    text-align: center;

    p {
      margin: 0;
      color: #c33;
    }
  }
}
</style>
